'use client';

import { tryit } from 'radash';
import { useState } from 'react';

import { makeFileSchema, type UploadFileResultType } from './types';

/**
 * Per-call overrides for upload validation and transfer. Omitted, validation matches the shared
 * default (10MB, standard document/image MIME set) and the PUT uses `file.type`. The file_upload
 * block passes `{ maxSize, anyType, contentType }` — `contentType` is the exact type the presigned
 * URL was signed for, which must match the PUT header (S3 rejects a mismatch), so a typeless file
 * uploaded as `application/octet-stream` doesn't fail after the round trip.
 */
type UploadOptions = { maxSize?: number; anyType?: boolean; contentType?: string };

/**
 * Uploads a file to the S3 presigned URL
 */
async function putFile(url: string, file: File, contentType: string) {
  return fetch(url, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': contentType,
    },
  }).then(res => {
    if (res.ok) {
      return res.text();
    }
    throw new Error('Failed to upload');
  });
}

export function useUploadFile() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const upload = async (presignedURL: UploadFileResultType, file: File, options?: UploadOptions) => {
    const validationResult = makeFileSchema(options).safeParse({ type: file.type, size: file.size });
    if (!validationResult.success) {
      setError(validationResult.error.errors[0].message);
      return {
        error: validationResult.error.errors[0].message,
      };
    }

    setLoading(true);

    const [err] = await tryit(putFile)(presignedURL.putFileUrl, file, options?.contentType ?? file.type);
    if (err) {
      setLoading(false);
      setError('Failed to upload');
      return {
        error: 'Failed to upload',
      };
    }
    setLoading(false);

    return {
      url: presignedURL.url,
      path: presignedURL.path,
    };
  };

  return {
    upload,
    loading,
    error,
  };
}

export * from './types';
